import { useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import {
  erpApi,
  type ErpPagination,
  type ErpUserRow,
  type ErpUsersOverview,
  type ErpUserStatus,
} from '../../api/erp';
import { StatCard } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { formatCount } from '../../utils/numbers';
import { formatDate } from '../../utils/format';
import './erp.css';

const STATUS_LABEL: Record<ErpUserStatus, string> = {
  activa: 'Activa',
  caducada: 'Caducada',
  cancelada: 'Cancelada',
  desactivada: 'Desactivada',
  pending_verification: 'Sin verificar',
};

function statusBadge(status: ErpUserStatus) {
  const tone =
    status === 'activa'
      ? 'success'
      : status === 'pending_verification'
        ? 'warning'
        : status === 'cancelada'
          ? 'danger'
          : 'neutral';
  return <Badge tone={tone}>{STATUS_LABEL[status] ?? status}</Badge>;
}

export function ErpUsersPage() {
  const [overview, setOverview] = useState<ErpUsersOverview | null>(null);
  const [users, setUsers] = useState<ErpUserRow[]>([]);
  const [pagination, setPagination] = useState<ErpPagination | null>(null);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [status, setStatus] = useState<'' | ErpUserStatus>('');
  const [page, setPage] = useState(1);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    erpApi
      .getUsersOverview()
      .then((res) => setOverview(res.overview))
      .catch((err) => setError(err instanceof Error ? err.message : 'Error al cargar'));
  }, []);

  useEffect(() => {
    setLoading(true);
    erpApi
      .listUsers({ search: search || undefined, status: status || undefined, page, limit: 25 })
      .then((res) => {
        setUsers(res.users);
        setPagination(res.pagination);
      })
      .catch((err) => setError(err instanceof Error ? err.message : 'Error al cargar'))
      .finally(() => setLoading(false));
  }, [search, status, page]);

  function applySearch(e: FormEvent) {
    e.preventDefault();
    setPage(1);
    setSearch(searchInput.trim());
  }

  return (
    <div>
      <div
        style={{
          marginBottom: '1.25rem',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: '1rem',
          flexWrap: 'wrap',
        }}
      >
        <div>
          <h1 style={{ margin: 0 }}>Clientes</h1>
          <p className="muted" style={{ margin: '0.35rem 0 0' }}>
            Cuentas de usuarios finales registradas en Hosty (excluye cuentas demo).
          </p>
        </div>
        <Link to="/erp/clientes/nuevo">
          <Button size="sm">Nuevo cliente</Button>
        </Link>
      </div>

      {error ? <p style={{ color: 'var(--color-danger)' }}>{error}</p> : null}

      {overview ? (
        <div className="erp-stats">
          <StatCard
            label="Cuentas totales"
            value={formatCount(overview.total)}
            hint={`${formatCount(overview.verified)} verificadas · ${formatCount(overview.unverified)} sin verificar`}
          />
          <StatCard
            label="Altas este mes"
            value={formatCount(overview.new_month)}
            hint={`${formatCount(overview.new_week)} esta semana · ${formatCount(overview.new_today)} hoy`}
          />
          <StatCard
            label="Con licencia"
            value={formatCount(overview.with_license)}
            hint={`${formatCount(overview.with_active_license)} con licencia activa`}
          />
          <StatCard
            label="Con verificación 2FA"
            value={formatCount(overview.with_mfa)}
            hint={overview.total > 0 ? `de ${formatCount(overview.total)} cuentas` : undefined}
          />
          <StatCard label="Activas" value={formatCount(overview.by_status.activa)} />
          <StatCard
            label="Sin verificar"
            value={formatCount(overview.by_status.pending_verification)}
          />
          <StatCard
            label="Canceladas / desactivadas"
            value={formatCount(overview.by_status.cancelada + overview.by_status.desactivada)}
            hint={`${formatCount(overview.by_status.caducada)} caducadas`}
          />
        </div>
      ) : null}

      <form
        onSubmit={applySearch}
        style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', margin: '1.25rem 0' }}
      >
        <input
          type="search"
          className="erp-filter-input"
          placeholder="Buscar por email o nombre…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
        />
        <select
          className="erp-filter-input"
          value={status}
          onChange={(e) => {
            setPage(1);
            setStatus(e.target.value as '' | ErpUserStatus);
          }}
        >
          <option value="">Todos los estados</option>
          <option value="activa">Activa</option>
          <option value="pending_verification">Sin verificar</option>
          <option value="caducada">Caducada</option>
          <option value="cancelada">Cancelada</option>
          <option value="desactivada">Desactivada</option>
        </select>
        <Button type="submit" size="sm" variant="ghost">
          Buscar
        </Button>
      </form>

      {loading ? <p>Cargando…</p> : null}

      <div className="erp-table-wrap">
        <table className="erp-table">
          <thead>
            <tr>
              <th>Cliente</th>
              <th>Estado</th>
              <th>2FA</th>
              <th>Licencias</th>
              <th>Alta</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr key={user.id}>
                <td>
                  <strong>
                    {user.name} {user.surname}
                  </strong>
                  <br />
                  <span className="muted" style={{ fontSize: '0.85rem' }}>
                    {user.email}
                  </span>
                </td>
                <td>{statusBadge(user.status)}</td>
                <td>{user.mfa_enabled ? <Badge tone="info">Sí</Badge> : <span className="muted">—</span>}</td>
                <td>
                  {user.license_count > 0 ? (
                    <>
                      {formatCount(user.license_count)}
                      {user.active_license_count > 0
                        ? ` · ${formatCount(user.active_license_count)} activa(s)`
                        : ''}
                    </>
                  ) : (
                    <span className="muted">—</span>
                  )}
                </td>
                <td>{formatDate(user.created_at)}</td>
              </tr>
            ))}
            {!loading && users.length === 0 ? (
              <tr>
                <td colSpan={5} className="muted" style={{ textAlign: 'center', padding: '1.5rem' }}>
                  No se encontraron clientes.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      {pagination && pagination.total_pages > 1 ? (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginTop: '1rem',
          }}
        >
          <span className="muted" style={{ fontSize: '0.85rem' }}>
            {formatCount(pagination.total)} clientes · página {pagination.page} de{' '}
            {pagination.total_pages}
          </span>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <Button
              size="sm"
              variant="ghost"
              disabled={pagination.page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Anterior
            </Button>
            <Button
              size="sm"
              variant="ghost"
              disabled={pagination.page >= pagination.total_pages}
              onClick={() => setPage((p) => p + 1)}
            >
              Siguiente
            </Button>
          </div>
        </div>
      ) : null}

      <p className="muted" style={{ fontSize: '0.8rem', marginTop: '1.5rem' }}>
        Datos tratados para la administración del servicio (art. 6.1.b RGPD). Solo se muestra la
        información mínima necesaria; no se exponen contraseñas ni secretos de seguridad.
      </p>
    </div>
  );
}
